import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { TIER_THEME, getTier } from '@/constants/tiers';
import StatusBadge from './StatusBadge';

/**
 * Status Progress card — Delta-style. Shows:
 *  - Current active tier (what perks the user has TODAY)
 *  - In-progress qualifying spend for the current calendar year
 *  - Projected status for the next benefit year
 *  - Visual progress bar + dollar gap to next tier
 *
 * Designed to be embedded:
 *  - Inside the Home miles card
 *  - As a section on the Rewards page
 *  - Stand-alone in the user account UI
 */
export default function StatusProgressCard({ status, variant = 'compact' }) {
  if (!status) return null;
  const active = status.active;
  const progress = status.in_progress;

  const activeTierId = active?.tier || 'member';
  const projectedTierId = progress?.projected_tier || 'member';
  const activeTheme = TIER_THEME[activeTierId];
  const activeTier = getTier(activeTierId);
  const projectedTier = getTier(projectedTierId);

  const isTopTier = !progress?.next_tier;
  const nextTier = progress?.next_tier ? getTier(progress.next_tier) : null;
  const dollarsToNext = progress?.dollars_to_next || 0;
  const percent = progress?.percent_to_next ?? 0;
  const qd = progress?.qd_dollars || 0;

  const benefitYearLabel = active
    ? new Date(active.benefit_period_start).getUTCFullYear()
    : null;
  const nextBenefitYear = (progress?.qualifying_year || new Date().getUTCFullYear()) + 1;

  const trackTint = variant === 'embedded' ? 'bg-black/30' : 'bg-white/5';
  const labelMuted = variant === 'embedded' ? 'text-white/45' : 'text-white/40';

  // Compact variant — fits inside dashboard-style glass cards (Home/Rewards)
  if (variant === 'compact' || variant === 'embedded') {
    return (
      <div className="space-y-3">
        {/* Top row: active tier + projected next-year tier */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-medium ${labelMuted} mb-1`}>Current Status{benefitYearLabel ? ` · ${benefitYearLabel}` : ''}</div>
            <StatusBadge tier={activeTierId} size="sm" showSparkle />
          </div>
          <div className="text-right">
            <div className={`text-[10px] uppercase tracking-widest font-medium ${labelMuted} mb-1`}>{nextBenefitYear} Status (Projected)</div>
            <StatusBadge tier={projectedTierId} size="sm" showSparkle />
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-[11px] font-medium mb-1.5">
            <span className="text-white/[0.55]">${Math.round(qd).toLocaleString()} earned this year</span>
            {!isTopTier && (
              <span className="text-white/[0.76]">
                ${Math.round(dollarsToNext).toLocaleString()} to {projectedTierId === nextTier?.id ? 'next' : nextTier.name}
              </span>
            )}
            {isTopTier && <span className="text-white/[0.76]">Top tier reached</span>}
          </div>
          <div className={`relative h-1.5 ${trackTint} rounded-full overflow-hidden`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className={`absolute inset-y-0 left-0 ${activeTheme.progressFill} rounded-full`}
            />
          </div>
        </div>
      </div>
    );
  }

  // Full variant — used on the Rewards page
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${activeTheme.border} bg-gradient-to-br ${activeTheme.gradient} ${activeTheme.glow}`}>
      <div className="p-6 sm:p-8 md:p-10">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Your Status{benefitYearLabel ? ` · ${benefitYearLabel}` : ''}</div>
            <h3 className={`text-3xl sm:text-4xl font-light tracking-tight ${activeTheme.accent}`}>
              {activeTier.name}
            </h3>
            <p className="text-sm text-white/60 font-normal mt-1">
              Earning at {activeTier.earnRate}
              {active?.granted_via === 'founding' && ' · Founding Member'}
              {active?.granted_via === 'manual' && ' · Granted by Defera'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">{nextBenefitYear} Projected</div>
            <StatusBadge tier={projectedTierId} size="lg" showSparkle />
            <p className="text-xs text-white/60 font-normal mt-2">
              Based on completed bookings this year
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-sm font-normal mb-2">
            <div>
              <span className="text-white/80 font-medium">${Math.round(qd).toLocaleString()}</span>
              <span className="text-white/50"> qualifying spend in {progress?.qualifying_year}</span>
            </div>
            {!isTopTier ? (
              <div className="text-white/70">
                <span className="font-medium">${Math.round(dollarsToNext).toLocaleString()}</span> to {nextTier.name}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-white/70">
                <TrendingUp className="w-4 h-4" /> Top tier reached
              </div>
            )}
          </div>
          <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 1.4, ease: 'easeOut' }}
              className={`absolute inset-y-0 left-0 ${activeTheme.progressFill} rounded-full`}
            />
          </div>
        </div>

        {/* Window note */}
        {active && (
          <p className="text-[11px] text-white/40 font-normal mt-4">
            Status valid {active.benefit_period_start} → {active.benefit_period_end}
          </p>
        )}
      </div>
    </div>
  );
}
